import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import styles from './RollenBeheer.module.css';

const PERMISSIE_MODULE_MAP = {
  'outputs.sbar':              'outputs.sbar',
  'outputs.artsconsult':       'outputs.artsconsult',
  'outputs.rapportage':        'outputs.rapportage',
  'outputs.dienst_overdracht': 'outputs.dienst_overdracht',
  'outputs.familie':           'outputs.familie',
  'outputs.overplaatsing':     'outputs.overplaatsing',
  'oefenmodus.gebruiken':      'oefenmodus',
  'oefenmodus.aanmaken':       'oefenmodus',
  'oefenmodus.casussen':       'oefenmodus',
  'rapportages.inzien':        'rapportages',
  'sessies.inzien_organisatie':      'org_outputs',
  'sessies.verwijderen_organisatie': 'org_outputs',
};

const PERMISSIE_GROEPEN = {
  'Outputs & Sessies': [
    { code: 'outputs.sbar',              label: 'SBAR genereren' },
    { code: 'outputs.artsconsult',       label: 'Artsconsult genereren' },
    { code: 'outputs.rapportage',        label: 'Verpleegkundige rapportage genereren' },
    { code: 'outputs.dienst_overdracht', label: 'Dienst-overdracht genereren' },
    { code: 'outputs.familie',           label: 'Familiecommunicatie genereren' },
    { code: 'outputs.overplaatsing',     label: 'Overplaatsingsoverdracht genereren' },
    { code: 'outputs.inzien',            label: 'Eigen outputs inzien' },
    { code: 'outputs.verwijderen',       label: 'Eigen outputs verwijderen' },
    { code: 'sessies.inzien_organisatie',      label: 'Alle sessies van organisatie inzien' },
    { code: 'sessies.verwijderen_organisatie', label: 'Alle sessies van organisatie verwijderen' },
  ],
  'Profiel': [
    { code: 'profiel.wachtwoord_wijzigen', label: 'Eigen wachtwoord wijzigen' },
  ],
  'Oefenmodus': [
    { code: 'oefenmodus.gebruiken',  label: 'Oefenmodus gebruiken' },
    { code: 'oefenmodus.casussen',   label: 'Casussen inzien en oefenen' },
    { code: 'oefenmodus.aanmaken',   label: 'Casussen aanmaken (docent)' },
  ],
  'Beheer': [
    { code: 'beheer.gebruikers',    label: 'Gebruikers beheren' },
    { code: 'beheer.rollen',        label: 'Rollen en permissies beheren' },
    { code: 'beheer.instellingen',  label: 'Organisatie-instellingen beheren' },
  ],
  'Rapportages': [
    { code: 'rapportages.inzien', label: 'Rapportages inzien' },
  ],
};

export default function RollenBeheer() {
  const { heeftPermissie, heeftModule } = useAuth();
  const isSuperadmin = heeftPermissie('superadmin.alles');
  const [organisaties, setOrganisaties]       = useState([]);
  const [geselecteerdeOrg, setGeselecteerdeOrg] = useState('');
  const [rollen, setRollen]           = useState([]);
  const [allePermissies, setAllePermissies] = useState([]);
  const [laden, setLaden]             = useState(true);
  const [toonModal, setToonModal]     = useState(false);
  const [geselecteerde, setGeselecteerde] = useState(null);
  const [formNaam, setFormNaam]       = useState('');
  const [formSysteemRol, setFormSysteemRol]   = useState(false);
  const [formTwofaVerplicht, setFormTwofa]     = useState(true);
  const [formPermissies, setFormPermissies] = useState([]);
  const [opslaan, setOpslaan]         = useState(false);

  const laadData = useCallback(async () => {
    try {
      const params = geselecteerdeOrg ? { organisatie_id: geselecteerdeOrg } : {};
      const [rolRes, permRes] = await Promise.all([
        axios.get('/api/gebruikers/rollen', { params }),
        axios.get('/api/rollen', { params }),
      ]);
      setRollen(rolRes.data.rollen.filter(r => r.naam !== 'Superadmin'));
      setAllePermissies(permRes.data.permissies || []);
    } catch {
      toast.error('Gegevens laden mislukt.');
    } finally {
      setLaden(false);
    }
  }, [geselecteerdeOrg]);

  useEffect(() => {
    if (isSuperadmin) {
      axios.get('/api/organisaties').then(r => setOrganisaties(r.data.organisaties)).catch(() => {});
    }
  }, [isSuperadmin]);

  useEffect(() => {
    if (!isSuperadmin || geselecteerdeOrg) laadData();
  }, [laadData, isSuperadmin, geselecteerdeOrg]);

  function openAanmaken() {
    setGeselecteerde(null);
    setFormNaam('');
    setFormPermissies([]);
    setFormSysteemRol(false);
    setFormTwofa(true);
    setToonModal(true);
  }

  function openBewerken(rol) {
    setGeselecteerde(rol);
    setFormNaam(rol.naam);
    setFormPermissies(rol.permissies || []);
    setFormSysteemRol(rol.is_systeem_rol === 1);
    setFormTwofa(rol.twofa_verplicht !== 0);
    setToonModal(true);
  }

  function togglePermissie(code) {
    setFormPermissies(prev =>
      prev.includes(code) ? prev.filter(p => p !== code) : [...prev, code]
    );
  }

  function selecteerGroep(groepPermissies, aan) {
    const codes = groepPermissies.map(p => p.code);
    if (aan) {
      setFormPermissies(prev => [...new Set([...prev, ...codes])]);
    } else {
      setFormPermissies(prev => prev.filter(p => !codes.includes(p)));
    }
  }

  async function handleOpslaan(e) {
    e.preventDefault();
    if (!formNaam.trim()) return toast.error('Rolnaam is verplicht.');
    setOpslaan(true);

    // Permissie IDs ophalen
    const permissieIds = allePermissies
      .filter(p => formPermissies.includes(p.code))
      .map(p => p.id);

    try {
      if (geselecteerde) {
        await axios.put(`/api/rollen/${geselecteerde.id}`, {
          naam: formNaam,
          permissie_ids: permissieIds,
          is_systeem_rol: formSysteemRol,
          twofa_verplicht: formTwofaVerplicht ? 1 : 0,
          organisatie_id: geselecteerdeOrg || undefined,
        });
        toast.success('Rol bijgewerkt.');
      } else {
        await axios.post('/api/rollen', {
          naam: formNaam,
          permissie_ids: permissieIds,
          is_systeem_rol: formSysteemRol,
          twofa_verplicht: formTwofaVerplicht ? 1 : 0,
          organisatie_id: geselecteerdeOrg || undefined,
        });
        toast.success('Rol aangemaakt.');
      }
      setToonModal(false);
      laadData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Opslaan mislukt.');
    } finally {
      setOpslaan(false);
    }
  }

  async function handleVerwijderen(rol) {
    if (rol.is_systeem_rol && !isSuperadmin) return toast.error('Systeemrollen kunnen niet worden verwijderd.');
    if (!window.confirm(`Weet u zeker dat u rol "${rol.naam}" wilt verwijderen?`)) return;
    try {
      await axios.delete(`/api/rollen/${rol.id}`);
      toast.success('Rol verwijderd.');
      laadData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Verwijderen mislukt.');
    }
  }

  if (laden && (!isSuperadmin || geselecteerdeOrg)) return <div className={styles.laden}>Laden...</div>;

  // Superadmin zonder organisatiekeuze
  if (isSuperadmin && !geselecteerdeOrg) {
    return (
      <div className={styles.pagina}>
        <div className={styles.header}>
          <div>
            <h1 className={styles.titel}>Rollenbeheer</h1>
            <p className={styles.subtitel}>Kies een organisatie</p>
          </div>
        </div>
        <div className={styles.orgKeuze}>
          {organisaties.map(org => (
            <button key={org.id} className={styles.orgKnop}
              onClick={() => setGeselecteerdeOrg(org.id.toString())}>
              <div className={styles.orgBolletje} style={{ background: org.primaire_kleur || '#3B5FC0' }} />
              <span className={styles.orgNaamTekst}>{org.naam}</span>
              <span className={styles.orgPijl}>→</span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.pagina}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.titel}>Rollenbeheer</h1>
          <p className={styles.subtitel}>{rollen.length} rol(len) in uw organisatie</p>
        </div>
        <div style={{display:'flex',gap:10}}>
          {isSuperadmin && (
            <button className={styles.knopSecundair} onClick={() => { setGeselecteerdeOrg(''); setRollen([]); }}>
              ← Andere organisatie
            </button>
          )}
          <button className={styles.knopPrimair} onClick={openAanmaken}>
            + Rol toevoegen
          </button>
        </div>
      </div>

      <div className={styles.rollenGrid}>
        {rollen.map(rol => (
          <div key={rol.id} className={styles.rolKaart}>
            <div className={styles.rolHeader}>
              <div>
                <div className={styles.rolNaam}>{rol.naam}</div>
                {rol.is_systeem_rol === 1 && (
                  <span className={styles.systeemBadge}>Systeemrol</span>
                )}
                {rol.twofa_verplicht === 0 && (
                  <span className={styles.twofaBadge}>2FA niet verplicht</span>
                )}
              </div>
              <div className={styles.rolActies}>
                <button className={styles.knopKlein} onClick={() => openBewerken(rol)}>
                  Bewerken
                </button>
                {!rol.is_systeem_rol && (
                  <button className={`${styles.knopKlein} ${styles.rood}`}
                    onClick={() => handleVerwijderen(rol)}>
                    Verwijderen
                  </button>
                )}
              </div>
            </div>
            <div className={styles.permissiesTags}>
              {(rol.permissies || []).length === 0 ? (
                <span className={styles.geenPermissies}>Geen permissies</span>
              ) : (
                (rol.permissies || []).map(p => (
                  <span key={p} className={styles.permissieTag}>{p.split('.')[1] || p}</span>
                ))
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Modal */}
      {toonModal && (
        <div className={styles.overlay} onClick={() => setToonModal(false)}>
          <div className={styles.modal} onClick={e => e.stopPropagation()}>
            <h2 className={styles.modalTitel}>
              {geselecteerde ? `${geselecteerde.naam} bewerken` : 'Nieuwe rol'}
            </h2>
            <form onSubmit={handleOpslaan} className={styles.form}>
              <div className={styles.veld}>
                <label>Rolnaam *</label>
                <input type="text" value={formNaam}
                  onChange={e => setFormNaam(e.target.value)}
                  placeholder="bijv. Teamleider" autoFocus required />
              </div>

              {isSuperadmin && (
		<>
                <label className={styles.checkLabel} style={{fontWeight:600, color:'#C65A00'}}>
                  <input type="checkbox" checked={formSysteemRol}
                    onChange={e => setFormSysteemRol(e.target.checked)} />
                  Systeemrol (kan niet worden verwijderd door organisatiebeheerder)
                </label>
                <label className={styles.checkLabel}>
                  <input type="checkbox" checked={formTwofaVerplicht}
                    onChange={e => setFormTwofa(e.target.checked)} />
                  2FA verplicht voor deze rol
                </label>
		</>
              )}
              <div className={styles.permissiesLabel}>Permissies</div>

              {Object.entries(PERMISSIE_GROEPEN).map(([groep, items]) => {
                // Filter permissies op actieve modules
                const zichtbaar = items.filter(p => {
                  const moduleCode = PERMISSIE_MODULE_MAP[p.code];
                  if (!moduleCode) return true;
                  return heeftModule(moduleCode);
                });
                if (zichtbaar.length === 0) return null;
                const allAan = zichtbaar.every(p => formPermissies.includes(p.code));
                return (
                  <div key={groep} className={styles.groep}>
                    <div className={styles.groepHeader}>
                      <span className={styles.groepNaam}>{groep}</span>
                      <button type="button" className={styles.groepToggle}
                        onClick={() => selecteerGroep(zichtbaar, !allAan)}>
                        {allAan ? 'Alles uit' : 'Alles aan'}
                      </button>
                    </div>
                    <div className={styles.checkGrid}>
                      {zichtbaar.map(p => (
                        <label key={p.code} className={styles.checkLabel}>
                          <input type="checkbox"
                            checked={formPermissies.includes(p.code)}
                            onChange={() => togglePermissie(p.code)} />
                          {p.label}
                        </label>
                      ))}
                    </div>
                  </div>
                );
              }).filter(Boolean)}

              <div className={styles.modalKnoppen}>
                <button type="button" className={styles.knopSecundair}
                  onClick={() => setToonModal(false)}>
                  Annuleren
                </button>
                <button type="submit" className={styles.knopPrimair} disabled={opslaan}>
                  {opslaan ? 'Opslaan...' : 'Opslaan'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
